import type { McpSetupResponse } from "@/mcp/types";

/**
 * The agent-ready setup prompt for the Routine MCP server: a self-contained
 * markdown document the user pastes into their AI agent (Cursor, Claude Code,
 * Codex, Claude Desktop, …), which then registers the server in its own
 * configuration and verifies the connection.
 *
 * The prompt embeds the local endpoint and the bearer token, so it is only
 * available once the token is (`null` otherwise) and must be treated as a
 * secret by the user.
 */
export function mcpSetupPrompt(setup: McpSetupResponse): string | null {
  const token = setup.token;
  if (!token) return null;
  const endpoint = setup.endpoint;

  return `# Connect this agent to Routine

Routine is a calendar-tasks-notes workspace: plan days and weeks, capture and
schedule tasks, manage events across calendars, take and link notes, and query
team tables. Its MCP server runs inside the Routine desktop app on this
machine — there is nothing to install, but the Routine app must be running and
logged in whenever the server is used.

- Endpoint: ${endpoint} (streamable HTTP, local only)
- Authorization header: \`Authorization: Bearer ${token}\`

The token above is a secret tied to this machine and to the Routine account
currently signed in (each account has its own token): write it only into the
MCP client configuration, never into chat logs, commits, or shared files.

Skip any step that already holds. If the server is already registered under
any name, use it as-is; never edit another agent's configuration and do not
add a duplicate entry.

## Register the MCP server

You are exactly one of the following clients; follow only the branch for the
client you are.

- **Cursor** — merge into \`~/.cursor/mcp.json\` (global; prefer global for a
  personal tool like Routine) or \`.cursor/mcp.json\` (this project only):

  \`\`\`json
  {
    "mcpServers": {
      "routine": {
        "url": "${endpoint}",
        "headers": { "Authorization": "Bearer ${token}" }
      }
    }
  }
  \`\`\`

- **Claude Code** —

  \`\`\`sh
  claude mcp add --transport http --scope user routine ${endpoint} \\
    --header "Authorization: Bearer ${token}"
  \`\`\`

- **Codex** — merge into \`~/.codex/config.toml\`:

  \`\`\`toml
  [mcp_servers.routine]
  url = "${endpoint}"
  http_headers = { "Authorization" = "Bearer ${token}" }
  \`\`\`

- **Claude Desktop** — bridge through \`mcp-remote\` (needs Node.js 18+);
  merge into \`claude_desktop_config.json\` (Settings → Developer → Edit
  Config). The header goes through an environment variable because Claude
  Desktop on Windows mangles spaces inside command arguments, and
  \`--allow-http\` is required because the endpoint is plain HTTP on the
  local loopback:

  \`\`\`json
  {
    "mcpServers": {
      "routine": {
        "command": "npx",
        "args": [
          "-y",
          "mcp-remote@latest",
          "${endpoint}",
          "--allow-http",
          "--transport",
          "http-only",
          "--header",
          "Authorization:\${ROUTINE_AUTH_HEADER}"
        ],
        "env": {
          "ROUTINE_AUTH_HEADER": "Bearer ${token}"
        }
      }
    }
  }
  \`\`\`

- **Any other MCP client running on this machine** — register a streamable
  HTTP server at the endpoint above, sending the Authorization header on
  every request.

- **Cloud-hosted assistants** (e.g. ChatGPT connectors) cannot reach a server
  on 127.0.0.1. If you are one, say so: Routine MCP needs an agent running on
  the same machine as the Routine desktop app.

After changing a configuration file, the client must pick it up: Cursor
reloads MCP servers automatically, Claude Code and Codex load them on the
next run, and Claude Desktop must be fully quit and reopened. Human-readable
setup instructions live at https://routine.co/plugins/mcp — relay that link
if the user prefers to configure their client by hand.

## Verify

Call the \`listOrganizations\` tool; it must return at least one organization.
Do not continue until this passes. On failure:

- 401 Unauthorized — the token rotated; ask the user to copy a fresh setup
  prompt from Routine (Settings → Plugins → MCP Server → Setup).
- 403 Routine is signed in to a different account — the token belongs to
  another Routine account on this machine; ask the user to switch accounts in
  Routine, or to copy the signed-in account's setup prompt.
- 503 Routine is not logged in — ask the user to open the Routine desktop app
  and sign in.
- Connection refused or 403 disabled — ask the user to enable the MCP server
  in Routine under Settings → Plugins → MCP Server.

## Rules that hold everywhere

- Routine IDs are opaque and kind-specific: never guess or construct one;
  resolve names with list/search tools first.
- Nothing is destructive without explicit user approval: deleting tasks,
  events, pages, or table rows needs a yes.
- Prefer human-readable summaries over raw JSON in every reply.
`;
}
